import { Flex, Skeleton, Typography } from '@iheartjane/reefer';
import type { ReactNode } from 'react';

import styles from './carouselHeader.module.css';

interface CarouselHeaderProps {
  afterHeader?: ReactNode;

  beforeHeader?: ReactNode;

  /** Carousel children, add carousel cards here */
  children?: ReactNode;

  /** HTML element for the carousel heading. Defaults to 'h2'. Use 'p' to suppress heading semantics (e.g. for sponsored rows). */
  headingAs?: 'h2' | 'h3' | 'p';

  /** Whether to display the carousel header's loading state */
  isLoading?: boolean;

  /** Name of the carousel, displayed as heading beside the controls */
  name: string;

  /** Small text below the carousel name */
  subtitle?: ReactNode;

  /** Small text above the carousel name */
  surtitle?: string;
}

type CarouselHeaderSkeletonProps = Omit<
  CarouselHeaderProps,
  'afterHeader' | 'beforeHeader' | 'isLoading' | 'name'
>;

function CarouselHeaderSkeleton({
  children,
  surtitle,
  subtitle,
}: CarouselHeaderSkeletonProps) {
  return (
    <div className={styles['headerContainer']}>
      <Flex flexDirection="column" width="50%" gap={8}>
        {surtitle && (
          <Skeleton.Bone width="50%" borderRadius="sm" height="16px" />
        )}
        <Skeleton.Bone
          width="100%"
          borderRadius="sm"
          className={styles['skeletonTitle']}
        />
        {subtitle && (
          <Skeleton.Bone width="50%" borderRadius="sm" height="16px" />
        )}
      </Flex>
      {children}
    </div>
  );
}

export function CarouselHeader({
  afterHeader,
  beforeHeader,
  children,
  headingAs = 'h2',
  isLoading = false,
  name,
  subtitle,
  surtitle,
}: CarouselHeaderProps) {
  if (isLoading) {
    return (
      <CarouselHeaderSkeleton subtitle={subtitle} surtitle={surtitle}>
        {children}
      </CarouselHeaderSkeleton>
    );
  }
  return (
    <div className={styles['headerContainer']}>
      <div className={styles['titleGroup']}>
        {surtitle && <Typography color="text-light">{surtitle}</Typography>}
        {!beforeHeader ? (
          <Typography as={headingAs} branded variant="header-bold">
            {name}
          </Typography>
        ) : (
          beforeHeader
        )}
        {subtitle ? (
          typeof subtitle === 'string' ? (
            <Typography color="text-light" mt={4}>
              {subtitle}
            </Typography>
          ) : (
            subtitle
          )
        ) : null}
      </div>

      {(children || afterHeader) && (
        <Flex alignItems="center">
          {children}
          {afterHeader ? afterHeader : null}
        </Flex>
      )}
    </div>
  );
}
